import React, { FC, useEffect, useRef, useState, useContext } from "react";
import { useDispatch } from "react-redux";
import { navigate } from "gatsby";
import * as Yup from "yup";
import Icon from "@icon-park/react/es/all";
import {
  Text,
  FilledButton as Button,
  Input,
  theme,
  PasswordInput,
} from "@ifgengineering/component-library";
import { Field, Formik, Form } from "formik";
import { Container, TermsLink, Link, OutlineButtonWithImage } from "../styled";
import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import { ErrorContainer } from "../MagicCode/styled";
import { useClientAuthComponents } from "../../../Context";
import { SignupFieldType, SignupProps } from "./types";
import GoogleIcon from "../../../../../assets/googleIcon.png";
import AppleIcon from "../../../../../assets/appleIcon.png";
import AppleLogin from "react-apple-login";
import { toast } from "react-toastify";

const Signup: FC<SignupProps> = ({
  onSubmit,
  title,
  errorMessage,
  loginUrl,
  signupRedirect,
  googleLogin,
  appleLogin,
  turnstile,
}) => {
  const { useAnalytics, signupAction, turnstileSiteKey, logException } = useClientAuthComponents();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const fireAnalytics = useAnalytics("signupAttempt");
  const firePageViewAnalytics = useAnalytics("pageView");
  const dispatch = useDispatch();
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const isTurnstileEnabled = turnstile?.enabled || false;
  const resetTurnstile = () => {
    if (!isTurnstileEnabled || !turnstileRef.current) {
      return;
    }
    try {
      turnstileRef.current.reset();
    } catch {
      // Ignore reset errors when widget isn't mounted/ready
    }
  };

  const SignupSchema = Yup.object().shape({
    password: Yup.string()
      .min(12, "Must be at least 12 characters")
      .max(128, "Please enter a password less than 128 characters")
      .required("Password is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    captchaToken: isTurnstileEnabled
      ? Yup.string().required("Please verify you are not a robot")
      : Yup.string().optional(),
  });

  useEffect(() => {
    const firePageView = async (eventName: string) => {
      (await firePageViewAnalytics)({ eventName });
    };

    firePageView("all:signup_pageViewed");
  }, []);

  const defaultSubmit = async (values: {
    email: string;
    password: string;
    captchaToken: string;
  }) => {
    const response = await dispatch(
      signupAction({
        email: values.email.toLowerCase(),
        password: values.password,
        signupRedirect,
        captchaToken: isTurnstileEnabled ? values.captchaToken : "",
      })
    );
    (await fireAnalytics)({
      email: values.email.toLowerCase(),
      method: "password",
    });

    if (response?.payload?.response?.status === 409) {
      setError("Email already exists");
      resetTurnstile();
    }

    if (response?.payload?.response?.status === 400) {
      resetTurnstile();
    }
  };

  const handleSubmit = async (values: {
    email: string;
    password: string;
    captchaToken: string;
  }) => {
    const submitFn = onSubmit || defaultSubmit;
    setIsLoading(true);
    await submitFn({ ...values });
    setIsLoading(false);
  };

  const isSocialLoginEnabled = googleLogin?.enable || appleLogin?.enable;

  return (
    <Formik
      enableReinitialize
      validationSchema={SignupSchema}
      initialValues={{
        email: "",
        password: "",
        captchaToken: "",
      }}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue }) => {
        return (
          <Form>
            <Container padding="24px 24px 0 24px">
              <Text type="T18" color="SLATE800" fontFamily="archiasemibold">
                {title || "Sign Up"}
              </Text>

              <Container gap={16}>
                <Field name="email">
                  {({ field, form: { errors, touched } }: SignupFieldType) => (
                    <Input
                      height="auto"
                      {...field}
                      testId="email-input"
                      onFocus={() => setError("")}
                      errorMessage={
                        (touched.email ? errors.email : undefined) ||
                        error ||
                        errorMessage
                      }
                      heading="Email"
                    />
                  )}
                </Field>

                <Field name="password">
                  {({ field, form: { errors, touched } }: SignupFieldType) => (
                    <PasswordInput
                      minNumCharacters={12}
                      {...field}
                      errorMessage={
                        touched.password ? errors.password : undefined
                      }
                      onChange={(value) => setFieldValue("password", value)}
                      heading="Create Password"
                    />
                  )}
                </Field>

                {isTurnstileEnabled && (
                  <Field name="captchaToken">
                    {({ form: { errors, touched } }: SignupFieldType) => (
                      <div data-testid="turnstile-widget">
                        <Turnstile
                          ref={turnstileRef}
                          siteKey={turnstileSiteKey}
                          onSuccess={(token) => {
                            setFieldValue("captchaToken", token);
                            // Add detectable marker for E2E tests
                            document.body.setAttribute(
                              "data-turnstile-verified",
                              "true"
                            );
                          }}
                          onError={() => {
                            setFieldValue("captchaToken", "");
                            document.body.removeAttribute(
                              "data-turnstile-verified"
                            );
                          }}
                          onExpire={() => {
                            setFieldValue("captchaToken", "");
                            document.body.removeAttribute(
                              "data-turnstile-verified"
                            );
                          }}
                          options={{
                            theme: "light",
                            size: "flexible",
                          }}
                        />
                        {touched.captchaToken && errors.captchaToken && (
                          <ErrorContainer>
                            <Icon
                              type="Caution"
                              size={14}
                              fill={theme.colors.ERROR800}
                              theme="filled"
                            />
                            <Text type="P14" color="ERROR800">
                              {errors.captchaToken}
                            </Text>
                          </ErrorContainer>
                        )}
                      </div>
                    )}
                  </Field>
                )}

                <Container gap={8}>
                  <Button
                    testId="signup-button"
                    id="signup-button"
                    type="submit"
                    height="56px"
                    text="Sign Up"
                    color="blue"
                    borderRadius="8px"
                    disabled={isLoading}
                    isLoading={isLoading}
                  />
                  {googleLogin?.enable && (
                    <OutlineButtonWithImage
                      text="Continue with Google"
                      image={GoogleIcon}
                      testId="google-login-button"
                      onClick={googleLogin?.onClick}
                      type="button"
                    />
                  )}
                  {appleLogin?.enable && (
                    <AppleLogin
                      clientId={appleLogin.clientId}
                      redirectURI={appleLogin.redirectURI}
                      usePopup={true}
                      responseMode="form_post"
                      responseType="code"
                      render={({ onClick }) => (
                        <OutlineButtonWithImage
                          text="Continue with Apple"
                          image={AppleIcon}
                          testId="apple-login-button"
                          onClick={onClick}
                          type="button"
                        />
                      )}
                      callback={(data) => {
                        if ("error" in data) {
                          const code =
                            typeof data.error === "string"
                              ? data.error
                              : data.error?.error;
                          if (
                            code === "user_cancelled_authorize" ||
                            code === "popup_closed_by_user"
                          ) {
                            return;
                          }
                          toast.error("Error authenticating with Apple.");
                          logException({
                            tag: "appleLogin",
                            exception:
                              typeof data.error === "string"
                                ? data.error
                                : JSON.stringify(data.error),
                          });
                          return;
                        }
                        appleLogin.onClick && appleLogin?.onClick(data);
                      }}
                    />
                  )}
                  <Container
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={4}
                    padding="0px 0px 24px 0px"
                  >
                    <Text type="S14" color="SLATE700">
                      Have an account?
                    </Text>{" "}
                    <Link
                      onClick={() => navigate(loginUrl || "/auth/login")}
                      fontSize="14px"
                    >
                      Login
                    </Link>
                  </Container>
                </Container>
              </Container>
            </Container>

            <Container
              backgroundColor="#E7EFF4"
              padding="8px 24px"
              borderBottomLeftRadius="16px"
              borderBottomRightRadius="16px"
            >
              <Text type="P12" color="SLATE800">
                By creating an account, you are agreeing to our{" "}
                <TermsLink
                  href="https://cur8.capital/wp-content/uploads/2025/07/Investment-Terms-Conditions-June-2025.pdf"
                  target="_blank"
                >
                  Terms and Conditions
                </TermsLink>
                {" and "}
                <TermsLink
                  href="https://www.islamicfinanceguru.com/privacy-policy"
                  target="_blank"
                >
                  Privacy Policy
                </TermsLink>
                . Cur8 Capital is authorised and regulated by the Financial
                Conduct Authority (FCA No. 943736)
              </Text>
            </Container>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Signup;
